import {
    Business as BusinessIcon,
    Description as DescriptionIcon,
    Email as EmailIcon,
    Engineering as EngineeringIcon,
    ExpandMore as ExpandMoreIcon, LocationOn as LocationIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Warning as WarningIcon
} from '@mui/icons-material'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { PrivateLoader } from 'src/components/templates'
import inspectionReportsActions from 'src/ducks/inspectionReports/actions'
import { getInspectionReportById } from 'src/ducks/selectors'
import { InspectionReportDetail } from 'src/ducks/types'
import { dateFormatString } from 'src/helpers'

const InspectionReportDetails = () => {
  const [loading, setLoading] = useState(false)
  const id = useParams().id || ''
  const dispatch = useDispatch()

  const report = useSelector(getInspectionReportById)

  const fetchInspectionReport = () => {
    setLoading(true)
    dispatch(inspectionReportsActions.fetchInspectionReportById(id, () => {
      setLoading(false)
    }))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchInspectionReport()
  }, [])

  const data = report as InspectionReportDetail

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'PLUMBING': '#2196F3',
      'ELECTRICAL': '#FF9800',
      'INTERIOR': '#4CAF50',
      'APPLIANCE': '#9C27B0',
      'EXTERIOR': '#795548',
      'HVAC': '#607D8B'
    }
    return colors[category] || '#757575'
  }

  const getContactIcon = (role: string) => {
    switch (role) {
      case 'Client':
        return <PersonIcon />
      case 'Inspector':
        return <EngineeringIcon />
      case 'Inspection Company':
        return <BusinessIcon />
      default:
        return <PersonIcon />
    }
  }

  return (
    <PrivateLoader loading={loading}>
      {!report ? <Typography variant="h4">We cannot find the inspection report information</Typography> : <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Grid container spacing={3}>
          {/* Header and General Information - Sticky */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              height: 'fit-content',
              position: 'sticky',
              top: 24,
              zIndex: 1
            }}>
              <CardContent>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Inspection Report
                </Typography>
                <Typography variant="h6" color="text.primary" sx={{ mb: 3 }}>
                  {data?.home_address}
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  General Information
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Estimate ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {data?.estimate_id}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Inspection Company
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {data?.inspection_company}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Inspection Date
                  </Typography>
                  <Typography variant="body1">
                    {dateFormatString(data?.inspection_date, 'MM/dd/yyyy')}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pages of Document
                  </Typography>
                  <Typography variant="body1">
                    {data?.pages} pages
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Document Type
                  </Typography>
                  <Chip
                    label={data?.document_type.replace('_', ' ')}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Processing Dates
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Created:</strong> {dateFormatString(data?.created_on, 'MM/DD/YYYY hh:mm a')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Started:</strong> {dateFormatString(data?.started_on, 'MM/DD/YYYY hh:mm a')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Completed:</strong> {dateFormatString(data?.completed_on, 'MM/DD/YYYY hh:mm a')}
                  </Typography>
                </Box>

                {data?.document_url && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Original Document
                    </Typography>
                    <Tooltip title="View document">
                      <IconButton
                        href={data?.document_url}
                        target="_blank"
                        color="primary"
                        sx={{ border: '1px solid #1976d2' }}
                      >
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Contacts and Notes */}
          <Grid item xs={12} md={8}>
            {/* Contacts */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Contacts
                </Typography>
                <Grid container spacing={2}>
                  {data?.contacts?.map((contact, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#1976d2' }}>
                            {getContactIcon(contact.role)}
                          </Avatar>

                          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {contact.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Chip
                              label={contact.role}
                              size="small"
                              color="secondary"
                              sx={{ alignSelf: 'flex-start' }}
                            />
                          </Box>

                          {contact?.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{contact.phone}</Typography>
                            </Box>
                          )}

                          {contact?.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{contact.email}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Inspection Notes and Predictions */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Inspection Notes and Predictions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {data?.notes.length} notes found with repair predictions
                </Typography>

                {data?.notes?.map((note, index) => (
                  <Accordion key={index} sx={{ mb: 2, border: '1px solid #e0e0e0', padding: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {note?.reference} - {note?.header}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {note?.locations?.map(loc => loc.location).join(', ')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={note?.status}
                            color={note?.status === 'COMPLETED' ? 'success' : 'warning'}
                            size="small"
                          />
                          {note?.summary_section_indicator && (
                            <Chip
                              label="Summary"
                              color="info"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={3}>
                        {/* Detalles de la Nota */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Problem Description
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {note?.note}
                          </Typography>

                          {note?.locations?.length > 0 && <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Locations
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {note?.locations?.map((location, locIndex) => (
                                <Chip
                                  key={locIndex}
                                  icon={<LocationIcon />}
                                  label={location.location}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>}

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Referenced Pages
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {note?.pageNumbers?.map((pageNum, pageIndex) => (
                                <Chip
                                  key={pageIndex}
                                  label={`Page ${pageNum.page_number}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>

                          {note?.images?.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Images ({note?.images?.length})
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {note?.images?.map((image, imgIndex) => (
                                  <Tooltip key={imgIndex} title="View image">
                                    <a
                                      href={image.image_path}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        border: '1px solid #e0e0e0',
                                        padding: '0.5px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <img src={image.image_path} alt="Inspection" style={{ width: 100, height: 100 }} />
                                    </a>
                                  </Tooltip>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Grid>

                        {/* Predicción - Sección Destacada */}
                        <Grid item xs={12} md={6}>
                          <Paper
                            sx={{
                              p: 3,
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #1976d2',
                              borderRadius: 2
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <WarningIcon sx={{ color: '#1976d2', mr: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                Repair Prediction
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Main Category
                              </Typography>
                              <Chip
                                label={note?.prediction?.predicted_category}
                                sx={{
                                  backgroundColor: getCategoryColor(note?.prediction?.predicted_category),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Repair Title
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {note?.prediction?.predicted_title}
                              </Typography>
                              <Chip
                                label={`Quantity: ${note?.prediction?.predicted_quantity}`}
                                size="small"
                                color="primary"
                              />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Category Match Reason
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {note?.prediction?.category_match_reason}
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Title Match Reason
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {note?.prediction?.title_match_reason}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Related Categories
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {note?.prediction?.categories?.map((category, catIndex) => (
                                  <Chip
                                    key={catIndex}
                                    label={category.category}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: getCategoryColor(category.category),
                                      color: getCategoryColor(category.category)
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                              <Typography variant="caption" color="text.secondary">
                                Prediction generated on: {dateFormatString(note?.created_at, 'MM/DD/YYYY hh:mm a')}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>}
    </PrivateLoader>
  )
}

export default InspectionReportDetails